import type { ControlId, ResolvedCamera } from '@/domain/types'
import { formatExpComp } from '@/domain/dials'

const SOURCE_NAMES: Record<ControlId | 'camera', string> = {
  camera: 'camera picks',
  shutterDial: 'shutter dial',
  isoDial: 'ISO dial',
  expCompDial: '± dial',
  mainCommandDial: 'rear dial',
  subCommandDial: 'front dial',
  modeLever: 'mode lever',
  photoVideoLever: 'photo/video',
  focalRing: 'zoom ring',
}

/**
 * The viewfinder strip. Each value says which control produced it — that
 * attribution is the whole teaching device.
 */
export function Readout({ resolved }: { resolved: ResolvedCamera }) {
  const dev = Math.max(-3, Math.min(3, resolved.meterDeviationEv))
  const offBy = Math.abs(resolved.meterDeviationEv)

  return (
    <div className="rounded-2xl border border-edge bg-panel/80 p-4">
      <div className="grid grid-cols-3 gap-3">
        <Value
          label="Shutter"
          value={resolved.shutter.label}
          source={SOURCE_NAMES[resolved.shutter.source]}
          auto={resolved.shutter.source === 'camera'}
        />
        <Value
          label="Aperture"
          value={resolved.aperture.label}
          source={SOURCE_NAMES[resolved.aperture.source]}
          auto={resolved.aperture.source === 'camera'}
        />
        <Value
          label="ISO"
          value={resolved.iso.label.replace('ISO ', '')}
          source={SOURCE_NAMES[resolved.iso.source]}
          auto={resolved.iso.source === 'camera'}
        />
      </div>

      <div className="mt-4">
        <div className="mb-1.5 flex items-baseline justify-between">
          <span className="text-[10px] font-semibold uppercase tracking-[.14em] text-muted">
            Exposure
          </span>
          <span
            className={`text-[11px] font-semibold tabular-nums ${
              offBy < 0.4 ? 'text-mint' : offBy < 1.5 ? 'text-amber' : 'text-nikon-red'
            }`}
          >
            {offBy < 0.4
              ? 'On the money'
              : `${offBy < 1.5 ? 'A bit' : 'Too'} ${
                  resolved.meterDeviationEv < 0 ? 'dark' : 'bright'
                }`}
          </span>
        </div>

        <div className="relative h-6 rounded-lg bg-ink/70 px-2">
          <div className="absolute inset-x-2 top-1/2 h-px -translate-y-1/2 bg-edge" />
          {[-3, -2, -1, 0, 1, 2, 3].map((tick) => (
            <div
              key={tick}
              className={`absolute top-1/2 -translate-y-1/2 ${
                tick === 0 ? 'h-4 w-px bg-muted' : 'h-2 w-px bg-edge'
              }`}
              style={{ left: `calc(${((tick + 3) / 6) * 100}% )` }}
            />
          ))}
          <div
            className={`absolute top-1/2 h-3.5 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full transition-[left] duration-200 ${
              offBy < 0.4 ? 'bg-mint' : offBy < 1.5 ? 'bg-amber' : 'bg-nikon-red'
            }`}
            style={{ left: `${((dev + 3) / 6) * 100}%` }}
          />
        </div>

        {resolved.exposureCompensation !== 0 && (
          <p className="mt-2 text-[11px] text-muted">
            Compensation {formatExpComp(resolved.exposureCompensation)} applied.
          </p>
        )}
      </div>
    </div>
  )
}

function Value({
  label,
  value,
  source,
  auto,
}: {
  label: string
  value: string
  source: string
  auto: boolean
}) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-[.14em] text-muted">{label}</div>
      <div
        className="mt-0.5 text-2xl font-bold tabular-nums leading-none"
        style={{ fontFamily: 'var(--font-dial)' }}
      >
        {value}
      </div>
      <div className={`mt-1 text-[10px] ${auto ? 'text-amber' : 'text-mint'}`}>{source}</div>
    </div>
  )
}
